import { useCallback, useMemo, useState } from "react"

import { Collapse, IconButton, Typography } from "@mui/material"

import { HelpOutlineRoundedIcon } from "@l2r-front/l2r-icons"

import { MoreMenu, MoreMenuItemConfig } from "../MoreMenu"

import * as Styled from "./CatalogListItem.styled"

type CatalogListItemProps = {
    children?: React.ReactNode,
    defaultIcon: string,
    disabled: boolean,
    expandable: boolean,
    highlighted: boolean,
    icon: string,
    label: string,
    moreMenuConfig?: MoreMenuItemConfig[],
    onClick: () => void,
    selected: boolean,
    startOpen?: boolean,
    tooltipText: string,
    ItemsCountComponent: React.ReactNode,
}

export function CatalogListItem(props: CatalogListItemProps) {

    const {
        children,
        defaultIcon,
        disabled,
        expandable,
        highlighted,
        icon,
        label,
        moreMenuConfig,
        onClick,
        selected,
        tooltipText,
        ItemsCountComponent,
    } = props

    const [openInternal, setOpenInternal] = useState(false)

    const isOpen = useMemo(() => {
        if (disabled) {
            return openInternal
        } else {
            return highlighted || selected
        }
    }, [disabled, highlighted, openInternal, selected])

    const displayChildren = !!(expandable && isOpen && children)
    const displayArrowIcon = expandable

    const onButtonClick = useCallback(() => {
        if (disabled) {
            setOpenInternal(prevState => !prevState)
        } else if (onClick) {
            onClick()
        }
    }, [disabled, onClick])

    const arrowIcon = useMemo(() => {
        return isOpen ? <Styled.ArrowUpIcon color="action/disabled" />
            : <Styled.ArrowDownIcon color="action/disabled" />
    }, [isOpen])

    return <>
        <Styled.ListItemButton key={Math.random()}
            disableRipple={disabled}
            isDisabled={disabled}
            highlighted={highlighted}
            onClick={onButtonClick}
            selected={selected}
        >
            <Styled.SideContainer maxWidth={"70%"}>
                {icon && <Styled.Image
                    isDisabled={disabled}
                    src={icon}
                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                        e.currentTarget.src = defaultIcon;
                    }}
                />}
                <Typography variant={(expandable && !disabled || selected || highlighted) ? "Desktop & Mobile/Heading/H3" : "Desktop & Mobile/Body/Regular"}>{label}</Typography>
            </Styled.SideContainer>
            <Styled.SideContainer>
                {tooltipText && <Styled.Tooltip displayArrowIcon={displayArrowIcon} disableFocusListener title={tooltipText}>
                    <IconButton>
                        <HelpOutlineRoundedIcon color="action/disabled" />
                    </IconButton>
                </Styled.Tooltip>}
                {disabled && !expandable && <Styled.ArrowText>-</Styled.ArrowText>}
                {!disabled && ItemsCountComponent}
                {displayArrowIcon && arrowIcon}
                {moreMenuConfig && <MoreMenu color="tertiary" menuConfig={moreMenuConfig} />}
            </Styled.SideContainer>
        </Styled.ListItemButton>
        <Collapse in={displayChildren}>
            <Styled.List disablePadding>
                {children}
            </Styled.List>
        </Collapse>
    </>
}
